import React, { useMemo, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from '../../../../types';
import {
  StyledInput,
  SearchIconContainer,
  ClearInputWrapper,
  ErrorWrapper,
  InputFieldWrapper,
  Container,
} from './Search.style';
import SearchIcon from './SearchIcon';
import CancelIcon from '../../../../assets/icons/CancelIcon';
import { useSearchModels } from './useSearchModels';
import SuggestionsList from './SuggestionsList';
import useClickOutside from '../../../../hooks/useClickOutside';
import CautionIcon from '../../../../assets/icons/CautionIcon';

interface SearchTypes {
  allOptions: string[];
  modelFamilyOptions: {
    [key: string]: FilterOption;
  };
  onChange: (newValue: string) => void;
}

const Search: React.FC<SearchTypes> = ({ allOptions, onChange, modelFamilyOptions }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number>(0);
  const searchTimer = useRef<number | null>(null);
  const { t } = useTranslation();

  const allOptionsList = useMemo(() => {
    return allOptions;
  }, [allOptions]);

  const { suggestions, fetchSuggestions, error, setError } = useSearchModels(
    allOptionsList,
    modelFamilyOptions,
  );
  const suggestionModels = useMemo(() => {
    return suggestions.map((model) => modelFamilyOptions[model]);
  }, [modelFamilyOptions, suggestions]);

  const handleSuggestionClick = (newValue: string) => {
    onChange(newValue);
    setInputValue('');
    fetchSuggestions('');
    setFocusedOptionIndex(0);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    setInputValue(input);
    if (focusedOptionIndex > 0) setFocusedOptionIndex(0);

    // Clear previous error
    setError(null);

    // Clear previous timer
    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    // Set a timer to fetch suggestions after a delay
    searchTimer.current = window.setTimeout(() => {
      fetchSuggestions(input);
    }, 400);
  };

  const handleEnterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && suggestionModels.length > 0) {
      // Handle selection of the selected/focused suggestion when user hits 'Enter'
      // If user focused/hovered a selection select that one otherwise select first Option
      if (focusedOptionIndex > 0) {
        handleSuggestionClick(suggestionModels[focusedOptionIndex].value);
      } else {
        handleSuggestionClick(suggestionModels[0].value);
      }
    }
    if (event.key === 'ArrowDown') {
      // Move focus to the next option
      event.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex < suggestions.length - 1 ? prevIndex + 1 : 0,
      );
    } else if (event.key === 'ArrowUp') {
      // Move focus to the previous option
      event.preventDefault();
      setFocusedOptionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : -1));
    } else if (event.key === 'Escape') {
      fetchSuggestions('');
    }
  };

  const handleFocusInput = () => {
    // Trigger Fetch Suggestion when the location and input value are already selected
    // And Then User Click/Focus on Input component
    if (inputValue) {
      fetchSuggestions(inputValue);
    }
  };

  const handleClearInput = () => {
    setInputValue('');
    fetchSuggestions('');
    setFocusedOptionIndex(0);
  };

  const handleClickOutsideDiv = useCallback(() => {
    fetchSuggestions('');
  }, [fetchSuggestions]);

  useClickOutside(wrapperRef, handleClickOutsideDiv);

  return (
    <>
      <Container ref={wrapperRef} data-testid="search-model-family-wrapper">
        <InputFieldWrapper>
          <SearchIconContainer>
            <SearchIcon />
          </SearchIconContainer>
          <StyledInput
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleEnterKey}
            onFocus={handleFocusInput}
            error={!suggestions.length && !!inputValue && !!error}
            type="text"
            placeholder={t('vlp.filters.models.searchPlaceholder')}
          />
          <ClearInputWrapper
            visible={!!inputValue.length}
            onClick={handleClearInput}
            data-testid="clear-input-button"
          >
            <CancelIcon />
          </ClearInputWrapper>
          {suggestionModels.length > 0 && (
            <SuggestionsList
              suggestions={suggestionModels}
              handleSuggestionClick={handleSuggestionClick}
              focusedOptionIndex={focusedOptionIndex}
              setFocusedOptionIndex={setFocusedOptionIndex}
            />
          )}
        </InputFieldWrapper>
        <ErrorWrapper visible={!suggestions?.length && !!inputValue && !!error}>
          <CautionIcon />
          {error}
        </ErrorWrapper>
      </Container>
    </>
  );
};

export default Search;
